<template>
  <!-- The whole row keeps an open mega menu open (see useMegaMenuOpen), like apple.com: the open panel sits inside it,
       and pb-4.5/-mb-4.5 stretch it down to the header's bottom edge, as the items do, so it reaches the panel. -->
  <div
    v-if="!settingsOnly"
    ref="navRow"
    class="hidden lg:flex gap-2 lg:gap-4 2xl:gap-8 grow items-center pb-4.5 -mb-4.5"
    @mouseover="stayInNavRow"
    @mouseleave="leaveNavRowAndItem">
    <!-- While a mega menu is open, every item but the active one (hovered, else open) is muted. -->
    <div
      v-for="item in items"
      :key="item.label"
      class="[&_[data-nav-item]]:transition-colors"
      :class="{ '[&_[data-nav-item]]:text-muted-foreground': isMutedItem(item, $t(item.label)) }"
      @mouseenter="enterItem($event, item)">
      <!-- The regExp in data-testid replaces all whitespaces with - symbols -->
      <MainMenuLink
        v-if="!item.items?.length"
        :item="item"
        :data-testid="`menu-item-${item.label.replace(/\s+/g, '-').toLowerCase()}`" />
      <MainMenuFlyout v-else v-slot="{ close }" :to="item.to" :label="$t(item.label)" :region="item.region">
        <div class="menu-sub">
          <!-- Remounted once the menu queries have loaded: the panel reads its item when it mounts. -->
          <MegaMenuPanel :key="String(menuLoading)" :item="item" @navigate="close" />
        </div>
      </MainMenuFlyout>
    </div>
    <!-- One underline for the whole row: it slides to the hovered item, the item whose mega menu is open, or the
         current section. The row isn't positioned (the mega menu panels position against the header), so the bar is
         placed against the same box. -->
    <span
      ref="underlineBar"
      aria-hidden="true"
      class="pointer-events-none absolute h-[5px] bg-current text-menu-link transition duration-300 ease-out"
      :style="underline" />
  </div>

  <TransitionRoot appear :show="open" as="template">
    <Dialog as="div" class="relative z-50 text-foreground" @close="closeMenu">
      <DialogBackground />
      <div class="fixed inset-0 overflow-hidden overflow-x-hidden z-50">
        <div class="h-full sm:h-auto sm:min-h-full flex justify-start items-start text-center text-foreground">
          <TransitionChild
            as="template"
            enter="transform transition ease-in-out duration-250 lg:duration-700"
            enter-from="-translate-x-full"
            enter-to="translate-x-0"
            leave="transform transition ease-in-out duration-250 lg:duration-700"
            leave-from="translate-x-0"
            leave-to="-translate-x-full">
            <DialogPanel
              ref="panel"
              class="w-full h-dvh sm:max-w-[400px] bg-mobile-menu text-search-forecolor text-left align-middle shadow-xl transition-all text-foreground pt-safe pb-safe flex flex-col gap-2 overflow-y-auto">
              <!-- Back (on a submenu level) and close. -->
              <div class="flex items-center justify-between px-3">
                <button
                  v-if="activeItem"
                  type="button"
                  class="flex size-11 items-center justify-center"
                  :aria-label="$t('Back')"
                  @click="back">
                  <ChevronLeftIcon class="size-7" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  class="flex size-11 items-center justify-center ms-auto"
                  :aria-label="$t('Close menu')"
                  @click="closeMenu">
                  <CloseIcon class="size-6" aria-hidden="true" />
                </button>
              </div>
              <Transition
                mode="out-in"
                enter-active-class="transition duration-200 ease-out"
                enter-from-class="translate-x-5 opacity-0"
                leave-active-class="transition duration-150 ease-in"
                leave-to-class="-translate-x-5 opacity-0">
                <!-- Level 2 / 3: the submenu of the opened menu item, as in the desktop mega menu. -->
                <MobileMenuLevel
                  v-if="activeItem"
                  :key="`${activeItem.label}-${activeEntryLabel}-${menuLoading}`"
                  class="ps-6 pe-3 pb-6"
                  :item="activeItem"
                  :entry-label="activeEntryLabel"
                  @open="openLevel(activeItem, $event)"
                  @navigate="closeMenu" />
                <!-- Level 1: the menu items, then currency, language and support. -->
                <div v-else class="flex flex-col gap-2 pb-6">
                  <template v-if="!settingsOnly">
                    <nav class="flex flex-col gap-2 ps-6 pe-3">
                      <template v-for="item in items" :key="item.label">
                        <button
                          v-if="item.items?.length"
                          type="button"
                          class="flex w-full min-h-11 items-center justify-between text-start text-xl font-semibold"
                          @click="openLevel(item)">
                          {{ $t(item.label) }}
                          <span class="flex size-11 shrink-0 items-center justify-center">
                            <ChevronRightIcon class="size-7 text-muted-foreground" />
                          </span>
                        </button>
                        <RouterLink
                          v-else
                          :to="item.to"
                          class="flex min-h-11 items-center text-xl font-semibold"
                          @click="closeMenu">
                          {{ $t(item.label) }}
                        </RouterLink>
                      </template>
                    </nav>
                    <hr class="ms-6 me-3 my-2 border-border" />
                  </template>
                  <MainMenuMobileSettings
                    class="ps-6 pe-3"
                    :hide-currency="settingsOnly"
                    @contact="openContactForm = true" />
                  <!-- Extra actions under the settings, e.g. the checkout's way back to the cart. -->
                  <div v-if="$slots.footer" class="ps-6 pe-3">
                    <slot name="footer" :close="closeMenu" />
                  </div>
                </div>
              </Transition>
              <ContactForm :open="openContactForm" @hide-modal="openContactForm = false" />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup lang="ts">
import { MainMenuFlyout, MainMenuLink } from ".";
import MegaMenuPanel from "./mega/MegaMenuPanel.vue";
import ContactForm from "@/components/ContactForm.vue";

import { ref, watch, computed, nextTick, onBeforeUnmount } from "vue";
import brandconfig from "@/brandconfig.ts";
import { toEvent } from "@/components/event";
import { toTeam, toTournament } from "@/components/events";
// import { useEventsStore } from "@/store/events.js";
// import { useSportTypesStore } from "@/store/sportTypes.js";
import { useRoute } from "vue-router";
import { useResizeObserver } from "@vueuse/core";
import { OPEN_DELAY, SWITCH_DELAY, useMegaMenuOpen } from "./mega/useMegaMenuOpen";
import MobileMenuLevel from "./mobile/MobileMenuLevel.vue";
import MainMenuMobileSettings from "./MainMenuMobileSettings.vue";
import { usePortalTournamentsCache } from "@/composables/tournaments";
import { useTournamentIdsWithEvents } from "@/composables/events";
import { usePortalEventsCache, PORTAL_EVENTS_CACHE_STALE_TIME } from "@/composables/events";
import { usePerformersByPerformerIds } from "@xs2event/js-lib/api";
import { useI18n } from "vue-i18n";
import { eventsWithPerformer } from "@/constants/routes";
import { slugify } from "@/helpers/format";
import { camelToSnake } from "@xs2event/js-lib/util";
import { useEvents } from "@/helpers/events";
import { useTeams } from "@/helpers/teams";
import type { MenuItem } from "./menuItem";
import { ChevronLeftIcon, ChevronRightIcon, CloseIcon, LanguageIcon } from "@xs2event/js-lib/icons";
import { TransitionRoot, TransitionChild, Dialog, DialogPanel } from "@headlessui/vue";
import DialogBackground from "@/components/shared/DialogBackground.vue";

const props = defineProps<{
  open: boolean;
  // The checkout: only language and support, nothing that leads away from it or changes its prices.
  settingsOnly?: boolean;
}>();

const emit = defineEmits(["close"]);

const closeMenu = () => {
  emit("close");
};

// Mobile drilldown: the opened menu item (level 2) and its opened entry, e.g. a country (level 3). Kept as labels so
// the levels follow `items` when its queries resolve after they were opened.
const activeItemLabel = ref<string>();
const activeEntryLabel = ref<string>();
const activeItem = computed(() => items.value?.find((item) => item.label === activeItemLabel.value));
const panel = ref();

const openLevel = (item, entryLabel?: string) => {
  activeItemLabel.value = item.label;
  activeEntryLabel.value = entryLabel;
  panel.value?.$el?.scrollTo?.({ top: 0 });
};

const back = () => {
  if (activeEntryLabel.value) activeEntryLabel.value = undefined;
  else activeItemLabel.value = undefined;
};

// Every time the drawer opens, it starts at the top level (reset on open, so closing doesn't flash level 1).
watch(
  () => props.open,
  (open) => {
    if (!open) return;
    activeItemLabel.value = undefined;
    activeEntryLabel.value = undefined;
  }
);

const searchMenuKey = ref(0);
const route = useRoute();
const openContactForm = ref(false);

watch(route, () => {
  searchMenuKey.value++;
  closeMenu();
});

const TODAY = new Date().toISOString().substr(0, 10);

// A menu item's featured column (popular teams / events) is fetched the same way as its submenus.
const submenusOf = (menuItem) => [...(menuItem.submenu ?? []), ...(menuItem.featured ? [menuItem.featured] : [])];

const queryParams = brandconfig.menu.reduce((current, menuItem) => {
  submenusOf(menuItem).forEach((submenu) => {
    const key = submenu.type;

    if (!current.has(key)) {
      current.set(key, new Map<string, Set<string>>());
    }

    const typeGroup = current.get(key)!;

    Object.entries(submenu.params).forEach(([key, value]) => {
      const actualKey = `${key}`;

      if (!typeGroup.has(actualKey)) {
        typeGroup.set(actualKey, new Set<string>());
      }

      if (typeof value === "string") {
        typeGroup.get(actualKey)!.add(value);
      } else {
        (value as string[]).forEach((val) => {
          typeGroup.get(actualKey)!.add(val);
        });
      }
    });
  });

  return current;
}, new Map<string, Map<string, Set<string>>>());

// date_stop >= today and sport-visibility scoping are already applied server side by the cache
// endpoint; each submenu's own params (tournament_id, sport_type, ...) are applied client side below.
const tournamentsQuery = queryParams.get("tournaments") && usePortalTournamentsCache();
// The cache endpoint returns camelCase fields; the rest of this component (filtering by
// submenu.params, toTournamentPage) expects snake_case, same as the other menu data sources.
// B2B: drop tournaments that would open on an empty events page, same as the tournaments page.
const tournamentIdsWithEvents = useTournamentIdsWithEvents();
const cachedTournaments = computed(() =>
  camelToSnake(
    (tournamentsQuery?.data.value?.tournaments ?? []).filter(
      (t) => !tournamentIdsWithEvents.value || tournamentIdsWithEvents.value.has(t.tournamentId)
    )
  )
);

// Portal events cache (same key as the login prefetch in App.vue): performer ids for the performers submenu, and the
// events of the season-events submenu.
const { locale } = useI18n();
const portalEventsQuery =
  (queryParams.get("performers") || queryParams.get("season-events")) &&
  usePortalEventsCache(computed(() => ({ acceptLanguage: locale.value })));

// Performers submenu: performer ids from the portal events cache, names from /performers
const performerSportTypes = Array.from(queryParams.get("performers")?.get("sport_type") ?? []);
const performerIds = computed(() => [
  ...new Set(
    (portalEventsQuery?.data.value?.events ?? [])
      .filter((e) => performerSportTypes.includes(e.sportType))
      .map((e) => e.performerId)
      .filter((id): id is string => !!id)
  ),
]);
const performersQuery =
  queryParams.get("performers") &&
  usePerformersByPerformerIds(performerIds, undefined, {
    enabled: computed(() => performerIds.value.length > 0),
    staleTime: PORTAL_EVENTS_CACHE_STALE_TIME,
  });

const popularEventsQuery =
  queryParams.get("popular-events") &&
  useEvents({
    ...Object.fromEntries(
      Array.from(queryParams.get("popular-events")!.entries()).map(([key, value]) => [
        key,
        value.size == 1 ? Array.from(value)[0] : `in:[${Array.from(value).join(",")}]`,
      ])
    ),
    date_stop: "ge:" + TODAY,
    sorting: "date_stop:asc",
    page_size: 5,
    popular_events: true,
    tickets_available: "ge:1",
    event_status: "in:[nosale,notstarted,postponed]",
  });

const upcomingEventsQuery =
  queryParams.get("upcoming-events") &&
  useEvents({
    ...Object.fromEntries(
      Array.from(queryParams.get("upcoming-events")!.entries()).map(([key, value]) => [
        key,
        value.size == 1 ? Array.from(value)[0] : `in:[${Array.from(value).join(",")}]`,
      ])
    ),
    date_stop: "ge:" + TODAY,
    sorting: "date_stop:asc",
    page_size: 5,
    popular_events: false,
    tickets_available: "ge:1",
    event_status: "in:[nosale,notstarted,postponed]",
  });

const popularTeamsQuery =
  queryParams.get("popular-teams") &&
  useTeams({
    ...Object.fromEntries(
      Array.from(queryParams.get("popular-teams")!.entries()).map(([key, value]) => [
        key,
        value.size == 1 ? Array.from(value)[0] : `in:[${Array.from(value).join(",")}]`,
      ])
    ),
    page_size: 9999,
    popular: true,
    event_startdate: "ge:2025-02-04",
  });

// The links of one submenu (or featured column), filtered on its params.
const resolveSubmenuItems = (submenu) => {
  let items = [];

  const { params } = submenu;

  if (submenu.type === "tournaments") {
    items = (
      cachedTournaments.value.filter((x) =>
        Object.entries(params).every(([key, value]) => {
          const contains = Array.isArray(value) ? value.includes(x[key]) : x[key] == value;

          return contains;
        })
      ) || []
    )
      .filter((x) => x.number_events)
      .map(toTournamentPage);
  }

  // One group per season (e.g. "Formula 1 2026") with its events in date order.
  if (submenu.type === "season-events") {
    const sportTypes = [params.sport_type].flat();
    const seasons = new Map<string, { label: string; to: string }[]>();
    (portalEventsQuery?.data.value?.events ?? [])
      .filter((event) => sportTypes.includes(event.sportType))
      .sort((a, b) => a.dateStart.localeCompare(b.dateStart))
      .forEach((event) => {
        const season = `${event.tournamentName} ${event.season}`;
        if (!seasons.has(season)) seasons.set(season, []);
        seasons.get(season)!.push({
          label: toShortEventName(event.eventName),
          to: toEvent({ slug: event.slug, event_id: event.eventId }),
        });
      });
    items = Array.from(seasons, ([label, events]) => ({ label, items: events }));
  }

  if (submenu.type === "performers") {
    items = (performersQuery?.data.value ?? [])
      .map((performer) => ({
        label: performer.name,
        to: eventsWithPerformer(slugify(performer.performerId), { sport_type: params.sport_type }),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  if (submenu.type === "popular-events") {
    items = (
      popularEventsQuery?.data.value?.events.filter((x) =>
        Object.entries(params).every(([key, value]) => {
          const propertyKey = key == "country" ? "iso_country" : key;

          const contains = Array.isArray(value) ? value.includes(x[propertyKey]) : x[propertyKey] == value;

          return contains;
        })
      ) || []
    ).map(toEventPage);
  }

  if (submenu.type === "upcoming-events") {
    items = (
      upcomingEventsQuery?.data.value?.events.filter((x) =>
        Object.entries(params).every(([key, value]) => {
          const propertyKey = key == "country" ? "iso_country" : key;

          const contains = Array.isArray(value) ? value.includes(x[propertyKey]) : x[propertyKey] == value;

          return contains;
        })
      ) || []
    ).map(toEventPage);
  }

  if (submenu.type === "popular-teams") {
    items = (
      popularTeamsQuery?.data.value?.teams.filter((x) =>
        Object.entries(params).every(([key, value]) => {
          const contains = Array.isArray(value) ? value.includes(x[key]) : x[key] == value;

          return contains;
        })
      ) || []
    ).map(toTeamPage);
  }

  return items;
};

// The nav items show straight away (from the brand config), so they don't pop in after a refresh. While the menu
// queries load, every submenu is kept (empty), so its item already shows as a mega menu item; once loaded, empty
// submenus and items without links are left out.
const menuLoading = computed(() =>
  [
    tournamentsQuery,
    portalEventsQuery,
    performersQuery,
    popularEventsQuery,
    upcomingEventsQuery,
    popularTeamsQuery,
  ].some((q) => q && q.isLoading.value)
);

const items = computed<MenuItem[]>(() => {
  const loading = menuLoading.value;

  return brandconfig.menu
    .map((menu) => ({
      ...menu,
      // Items with a submenu link to the events page filtered on their sport type(s).
      to: menu.to || menu.flyout_link?.to,
      label: menu.label,
      items: menu.submenu
        ?.map((submenu) => {
          const items = resolveSubmenuItems(submenu);
          return ((loading || submenu.to || items.length) && { ...submenu, items }) || undefined;
        })
        .filter(Boolean),
      featured: menu.featured && {
        label: menu.featured.label,
        links: mixCountries(resolveSubmenuItems(menu.featured)).slice(0, menu.featured.limit),
      },
    }))
    .filter((x) => x.items?.length || x.to);
});

// "Spanish Grand Prix (Madrid)" -> "Madrid", "Bahrain Grand Prix 2027" -> "Bahrain", "DutchGP" -> "Dutch": the season
// is in the group label.
const toShortEventName = (name: string) =>
  name.match(/\(([^)]+)\)/)?.[1] ??
  name
    .replace(/\s*(?:Grand Prix|GP)\b/, "")
    .replace(/\b\d{4}\b/, "")
    .trim();

const toEventPage = (event) => ({
  label: event.event_name,
  to: toEvent(event),
});

const toTeamPage = (team) => ({
  label: team.official_name,
  to: toTeam(team),
  country: team.iso_country,
});

// Takes one link per country in turn (in order of first appearance), so a featured column isn't filled by the teams
// of a single country. Links without a country keep their order.
const mixCountries = (links) => {
  const byCountry = new Map<string, unknown[]>();
  links.forEach((link) => {
    const key = link.country ?? "";
    byCountry.set(key, [...(byCountry.get(key) ?? []), link]);
  });
  const queues = Array.from(byCountry.values());
  const mixed = [];
  for (let round = 0; mixed.length < links.length; round++) {
    queues.forEach((queue) => round < queue.length && mixed.push(queue[round]));
  }
  return mixed;
};

// we can have multiple MS/F1/Moto season sales running
const toTournamentPage = (tournament) => {
  const label = ["motosport", "motogp", "formula1"].includes(tournament.sport_type)
    ? tournament.official_name + " (" + tournament.season + ")"
    : tournament.official_name;

  const isInternational =
    tournament.official_name?.toLowerCase().includes("champions league") ||
    tournament.official_name?.toLowerCase().includes("europa league") ||
    tournament.official_name?.toLowerCase().includes("conference league") ||
    tournament.official_name?.toLowerCase().includes("uefa champions") ||
    tournament.official_name?.toLowerCase().includes("uefa europa") ||
    tournament.official_name?.toLowerCase().includes("uefa conference");

  return {
    label,
    region: tournament.region,
    season: tournament.season,
    number_events: tournament.number_events,
    to: toTournament(tournament),
    icon: isInternational ? LanguageIcon : undefined,
  };
};

// Desktop nav underline: one bar under the hovered item, else the item whose mega menu is open, else the current
// section (`data-nav-active`). Items mark where the bar goes with `data-nav-item`.
// Kept below the helpers above: watching `items` evaluates it during setup, and once the menu queries are cached
// (e.g. coming back from the event page) that calls the helpers, which must be initialised by then.
const navRow = ref<HTMLElement>();
const underlineBar = ref<HTMLElement>();
const hoveredItem = ref<HTMLElement | null>(null);
const { openFlyout, hoverFlyout, stayInNavRow, leaveNavRow } = useMegaMenuOpen();

const hoveredLabel = ref<string | null>(null);

// The underline moves to a hovered item after the same delay as its mega menu opens (or swaps in), so they move together.
let hoverTimer: ReturnType<typeof setTimeout> | undefined;

const enterItem = (event: MouseEvent, item: MenuItem) => {
  const target = event.currentTarget as HTMLElement;
  clearTimeout(hoverTimer);
  hoverTimer = setTimeout(
    () => {
      hoveredItem.value = target;
      hoveredLabel.value = item.label;
    },
    openFlyout.value === null ? OPEN_DELAY : SWITCH_DELAY
  );
  // An item without a mega menu closes the open one (an item with one swaps it, from MainMenuFlyout).
  if (!item.items?.length) hoverFlyout(null);
};

const leaveNavRowAndItem = () => {
  clearTimeout(hoverTimer);
  hoveredItem.value = null;
  hoveredLabel.value = null;
  leaveNavRow();
};

// `openFlyout` holds the translated label (as MainMenuFlyout gets it), `hoveredLabel` the item's own label.
const isMutedItem = (item: MenuItem, translatedLabel: string) => {
  if (openFlyout.value === null) return false;
  return hoveredLabel.value !== null ? hoveredLabel.value !== item.label : openFlyout.value !== translatedLabel;
};
const underline = ref<Record<string, string | number>>({ opacity: 0 });

const underlineTarget = () =>
  hoveredItem.value?.querySelector("[data-nav-item]") ??
  (openFlyout.value && navRow.value?.querySelector(`[data-nav-item="${CSS.escape(openFlyout.value)}"]`)) ??
  navRow.value?.querySelector("[data-nav-active]");

const updateUnderline = () => {
  const target = underlineTarget();
  const container = underlineBar.value?.offsetParent;
  if (!target || !container) {
    underline.value = { ...underline.value, opacity: 0 };
    return;
  }
  const box = container.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  underline.value = {
    left: `${rect.left - box.left}px`,
    width: `${rect.width}px`,
    top: `${rect.bottom - box.top - 5}px`,
    opacity: 1,
    // From hidden it appears in place; only a visible bar slides.
    transitionProperty: underline.value.opacity ? "left, width, opacity" : "opacity",
  };
};

watch([hoveredItem, openFlyout, () => route.fullPath, items], () => nextTick(updateUnderline));
useResizeObserver(navRow, updateUnderline);
onBeforeUnmount(() => clearTimeout(hoverTimer));
</script>

<style scoped></style>
